import { SyntheticEvent } from "react";
import { Action, Store } from "redux";
import { SagaMiddleware, Task } from "redux-saga";

import { IDataReaderClient } from "@multiweb/clients/dataReader";
import { IBridge, ICryptographicInfo, ISignatureInfo, NAME } from "@multiweb/reactCore/bridge/api";
import { doNothing } from "@multiweb/reactUtils/constants"; // přímý import kvůli cyklické závislosti mezi bridge a utils
import { IAttrValue, ILanguageExtended, ILocalizationItem, IPublicAttributeInfo, IRunMethodResult } from "@multiweb/types/contracts";
import {
    CancellationToken,
    EventCallback,
    ExperimentName,
    IAppConfiguration,
    IDataEventsManager,
    IDefferredWindow,
    IEnumType,
    IMenuNavigationConfig,
    IOWObject,
    IOWObjectList,
    IParticipant,
    IPopupMenu,
    ITypeFactory,
    IVisible,
    IWorkflowMenuItem,
    RequestContext,
    RunStaticMethodParams,
    RunStaticMethodWithParamsConfig,
    SignatureInfoType,
    SignatureValidationMsg,
    TypeIdentifier,
} from "@multiweb/types/multiweb";
import {
    ApplicationModule,
    IAppState,
    ObjectType,
    OpenEditWindowParams,
    ReactAction,
    ReactActionId,
    SagaWithReturnType,
} from "@multiweb/types/react";
import { Tail } from "@redux-saga/core/effects";
import { StrictBridge } from "../strict";
import { createIsLocalStorageKeyLoadedActionPattern } from "../utils";

const MOCK_VALUE = "MOCK_VALUE";

/**
 * Mock implementace bridge modulu. Poskytuje plnohodnotnou implementaci ze {@link StrictBridge}.
 * Ostatní funkce buď nedělají nic, nebo vrací dummy hodnoty. Nezpracovává žádné příchozí akce.
 *
 * Některé návratové hodnoty nemusí mít plně definované typy. Pokud potřebujete s takovými hodnotami pracovat,
 * poskytněte vlastní implementaci {@link IBridge} a nastavte ji do {@link IProxyBridge.setBridgeImpl}.
 */
export class MockBridge extends StrictBridge implements IBridge {
    public get module(): ApplicationModule<typeof NAME> {
        return {
            NAME,
        };
    }

    public readonly setStore = (_storeParam: Store<IAppState>, _runSaga: SagaMiddleware["run"]): void => {
        return;
    };

    public readonly errorHandler = (_error: unknown): void => {
        return;
    };

    public readonly dispatchAction = (_action: Action): void => {
        return;
    };

    public readonly runSaga = <S extends SagaWithReturnType<any[]>>(_saga: S, ..._args: Parameters<S>): Task => {
        return undefined as unknown as Task;
    };

    public readonly getState = (): IAppState => {
        return undefined as unknown as IAppState;
    };

    public readonly useSelector = <Fn extends(state: IAppState, ...args: Tail<Parameters<Fn>>) => ReturnType<Fn>>(_selector: Fn, ..._args: Tail<Parameters<Fn>>): ReturnType<Fn> => {
        return undefined as unknown as ReturnType<Fn>;
    };

    public readonly getDataEventsManager = (): IDataEventsManager | undefined => {
        return undefined;
    };

    public readonly getDIContainer = (): ITypeFactory => {
        return {
            create<T>(_typeIdentifier: TypeIdentifier<T>, ..._params: any[]): T {
                return {} as T;
            },
            has<T>(_typeIdentifier: TypeIdentifier<T>): boolean {
                return false;
            },
        } as ITypeFactory;
    };

    public readonly getAppConfiguration = (): IAppConfiguration => {
        return undefined as unknown as IAppConfiguration;
    };

    public readonly isExperimentEnabled = (_experimentName: ExperimentName): boolean => {
        return false;
    };

    public readonly createIsLocalStorageKeyLoadedActionPattern = (key: string): (action: Action) => boolean => {
        return createIsLocalStorageKeyLoadedActionPattern(key);
    };

    public readonly getAttributeInfo = (_objectName: string, _attributesToCheck: string[]): Promise<readonly IPublicAttributeInfo[]> => {
        return Promise.resolve([]);
    };

    public readonly readAttributeValue = (_attrValue: IAttrValue | ObjectType | null | undefined, _explicitKnownTypes?: any[]): any => {
        return undefined;
    };

    public readonly getDataReaderClient = (): IDataReaderClient => {
        return undefined as unknown as IDataReaderClient;
    };

    public readonly runStaticMethodWithParams = (_runStaticMethodWithParamsConfig: RunStaticMethodWithParamsConfig, _cancellationToken?: CancellationToken): Promise<IRunMethodResult> => {
        return Promise.resolve({} as IRunMethodResult);
    };

    public readonly createObjectList = (): IOWObjectList => {
        return { add: doNothing } as unknown as IOWObjectList;
    };

    public readonly getActivityParticipants = (_instanceId: string, _objectList: IOWObjectList, _activityId: string, _requestContext?: RequestContext): Promise<IParticipant[]> => {
        return Promise.resolve([]);
    };

    public readonly getDocumentDownloadUrlByObjectId = (_dataObjectId: string, _objectName: string, _attributeName: string): Promise<string | null> => (
        Promise.resolve(null)
    );

    public readonly runStaticMethod = (_config: RunStaticMethodParams, _cancellationToken: CancellationToken): Promise<IRunMethodResult> => (
        Promise.resolve({
            isInLongTransaction: false,
            resultValue: undefined,
            messages: [],
        })
    );

    public readonly isWFDefinedToClass = (_className: string, _requestContext?: RequestContext, _force: boolean = false): Promise<boolean> => {
        return Promise.resolve(true);
    };

    public readonly objectsWFMenu = (_objecList: IOWObjectList, _requestContext?: RequestContext): Promise<IWorkflowMenuItem[]> => {
        return Promise.resolve([{
            ItemName: "itemName",
            ID: "id",
            Codename: "codename",
            ParticipantsSelecting: true,
            ValidatePostReq: true,
            ValidateRecommended: true,
            MasterNode: "MasterNode",
            NextActivityID: "NextActivityID",
            ParamFormName: "ParamFormName",
            LinkAttrForSelObj: "LinkAttrForSelObj",
            SetValues: "SetValues",
            ConfirmMessage: "ConfirmMessage",
            Order: 0,
        } as IWorkflowMenuItem]);
    };

    public readonly getInstance = (_instanceId: string): IOWObject | null => {
        return null;
    };

    public readonly prepareDownload = (_url: string): Promise<boolean> => Promise.resolve(false);

    public readonly getEnumData = (_enumName: string): Promise<IEnumType> => Promise.resolve(undefined as unknown as IEnumType);

    public readonly stopPropagation = (event: SyntheticEvent): void => {
        event.stopPropagation();
        return;
    };

    public readonly stopPropagationNative = (event: Event): void => {
        event.stopPropagation();
        return;
    };

    public readonly generateElementID = (): string => {
        return MOCK_VALUE;
    };

    public readonly openUrl = (url: string, target?: string) => {
        window.open(url, target);
        return;
    };

    public readonly createPopupMenu = (_contentElement?: HTMLElement, _cancelByClick?: boolean): IPopupMenu => {
        return {
            setVisibility: doNothing,
            dispose: doNothing,
            arangePopupToEditableControl: doNothing,
            setIndent: doNothing,
            visible: false,
            element: null as unknown as HTMLElement,
        };
    };

    public readonly logUserInteractionEvent = (_event: Event): Promise<void> => {
        return Promise.resolve(undefined);
    };

    public readonly getControlComponentVisibility = (_componentId: string): boolean => {
        return false;
    };

    public readonly addControlComponentVisibilityChangedListener = (_componentId: string, _listener: EventCallback<IVisible, boolean>): void => {
        return;
    };

    public readonly removeControlComponentVisibilityChangedListener = (_componentId: string, _listener: EventCallback<IVisible, boolean>): void => {
        return;
    };

    public readonly loadLocalizationItems = (_locKeys: readonly string[]): Promise<Map<string, ILocalizationItem>> => {
        return Promise.resolve(new Map());
    };

    public readonly getSupportedLanguages = (): Promise<readonly ILanguageExtended[]> => {
        return Promise.resolve([]);
    };

    public readonly resetRefreshTimeout = (_dataSourceKey: string): void => {
        return;
    };

    public readonly executeReactConnectorAction = (_dataSourceKey: string, _actionId: ReactActionId, _dataObjectId: string): void => {
        return;
    };

    public readonly openReactConnectorDetail = (_dataSourceKey: string, _dataObjectId: string): Promise<void> => {
        return Promise.resolve(undefined);
    };

    public readonly openAttributeEditWindow = (_params: OpenEditWindowParams): Promise<void> => {
        return Promise.resolve(undefined);
    };

    public readonly setReactConnectorMultiselectMode = (_dataSourceKey: string, _multiselectMode: boolean): void => {
        return;
    };

    public readonly getReactConnectorRowActions = (_dataSourceKey: string): ReadonlyArray<ReactAction> => {
        return [];
    };

    public readonly getMenuNavigationConfig = (): IMenuNavigationConfig | undefined => {
        return undefined;
    };

    public override readonly stringToBoolean = (value: string | null | undefined, defaultReturn: boolean): boolean => {
        return value ? value.toLowerCase() === "true" : defaultReturn;
    };

    public isSignatureEmpty = (signatureInfo: ISignatureInfo): boolean => {
        return !signatureInfo || signatureInfo.type === SignatureInfoType.empty;
    };

    public isSigned = (signatureInfo: ISignatureInfo): boolean => {
        return signatureInfo && signatureInfo.type !== SignatureInfoType.empty;
    };

    public isEncrypted = (cryptoInfo: ICryptographicInfo): boolean => {
        if (cryptoInfo?.encryptionInfo?.encrypted) {
            return true;
        }
        const validationMessage = cryptoInfo?.signatureInfo?.validationMessage as SignatureValidationMsg;
        if ([SignatureValidationMsg.IsEncrypted, SignatureValidationMsg.AttributeEncrypted, SignatureValidationMsg.AllowUnknownSignature].includes(validationMessage)) {
            return true;
        }
        return false;
    };

    public readonly hasSignatureWarning = (signatureInfo: ISignatureInfo | null | undefined, withCounterSignatures?: boolean): boolean => {
        if (!signatureInfo) {
            return false;
        }
        if (withCounterSignatures && signatureInfo.counterSignature) {
            for (const cs of signatureInfo.counterSignature)
                if (this.hasSignatureWarning(cs))
                    return true;
        }
        if (signatureInfo.valid == null && signatureInfo.validationMessage?.length && signatureInfo.validationMessage !== SignatureValidationMsg.EmptySignature) {
            return true;
        }
        if (signatureInfo.valid === false && signatureInfo.validationMessage) {
            switch (signatureInfo.validationMessage) {
                case SignatureValidationMsg.Failed:
                case SignatureValidationMsg.Error:
                case SignatureValidationMsg.Empty:
                    break;
                default:
                case null:
                case "":
                    return true;
            }
        }
        return false;
    };

    public readonly canCallOnlyStaticMethods = (_url: string, _functions: ReadonlyArray<string>): boolean => {
        return false;
    };

    public readonly createDefferedWindow = () => {
        return {} as IDefferredWindow;
    };

    public readonly objectWFActionExecuteWithoutInstance = (_ids: string[], _className: string, _actionID: string, _nextParticipants: IParticipant[], _requestContext?: RequestContext): Promise<IRunMethodResult> => {
        return Promise.resolve({} as IRunMethodResult);
    };
}
